/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { Mail, UserPlus } from "lucide-react";
import {
  familyDetails,
  searchAccounts,
  type AccountSummary,
  type FamilyDetails,
  type FamilyMemberDetail,
} from "../api/accounts";
import {
  addToCareTeam,
  coverageLabel,
  familyCareTeam,
  removeFromCareTeam,
  setCoverage,
  COVERAGE_OPTIONS,
  type CareTeamAssignment,
  type CareTeamMember,
  type ChildCareTeam,
  type Coverage,
} from "../api/careTeam";
import { newIdempotencyKey, type ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { GuardedButton } from "../components/ui/GuardedButton";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { ToggleButton } from "../components/ui/ToggleButton";
import { BusyLabel } from "../components/ui/BusyLabel";
import { CoverageChoice } from "../components/CoverageChoice";
import { FamilyGlance, childName, householdName, memberName } from "../components/FamilyGlance";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import {
  formatWhen,
  fullName,
  isCareStaff,
  roleLabel,
  statusLabel,
} from "../lib/accounts";
import { usePolling } from "../lib/usePolling";

const IN_FLIGHT = "A change for this person is still in progress. Try again in a minute.";
const MESSAGING_TIMING = "The family can usually message them within a minute.";
const CARE_TEAM_POLL_MILLIS = 5_000;
/** Healthie takes a change within seconds; one still unapplied after this has failed in the outbox. */
const PENDING_TIMEOUT_MILLIS = 2 * 60_000;

/** The household in one place: members with their account state, care, and pending invites. */
export function FamilyDetail() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [details, setDetails] = useState<FamilyDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDetails(null);
    setError(null);
    if (id === undefined) {
      return;
    }
    void familyDetails(id).then((response) => {
      if (response.ok) {
        setDetails(response.value);
      } else {
        setError(response.error.message);
      }
    });
  }, [id]);

  const familyName = details ? householdName(details) : `Family ${id ?? ""}`;
  useChrome(
    {
      title: familyName,
      back: { to: "/care/families", label: "Families" },
      sidePanel:
        details !== null && details.invites.length > 0 ? (
          <FamilyAside details={details} />
        ) : undefined,
    },
    [familyName, details],
  );

  if (error !== null) {
    return <ErrorMessage message={error} className="mt-8" />;
  }
  if (details === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          {familyName}
        </h1>
      </header>
      <FamilyGlance
        details={details}
        careTeam={isCareStaff(hasRole) ? <CareTeamSection details={details} /> : null}
      />
    </div>
  );
}

function FamilyAside({ details }: { details: FamilyDetails }) {
  return (
    <aside className="space-y-5">
      {details.invites.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-text-tertiary">
            Pending invites
          </h2>
          <ol className="space-y-2">
            {details.invites.map((invite) => (
              <li key={invite.id} className="border border-border bg-bg-elevated shadow-card rounded-2xl px-4 py-3 text-sm">
                <span className="inline-flex min-w-0 items-center gap-2 text-text-secondary">
                  <Mail size={14} className="shrink-0 text-text-tertiary" />
                  <span className="truncate">{invite.email}</span>
                </span>
                <p className="mt-1 text-xs text-text-tertiary">
                  Sent {formatWhen(invite.createdAt)}
                </p>
              </li>
            ))}
          </ol>
        </div>
      )}
    </aside>
  );
}

type PendingChange = { staffUserId: number; name: string; since: number } & (
  | { change: "add"; childUserIds: number[] }
  | { change: "remove" }
);

function expired(change: PendingChange, now: number): boolean {
  return now - change.since > PENDING_TIMEOUT_MILLIS;
}

function refusalMessage(error: ApiError): string {
  return error.httpStatus === 409 ? IN_FLIGHT : error.message;
}

/** The children an add writes to: care recipients, not disabled, with an active Healthie record. */
function activeChildIds(details: FamilyDetails): number[] {
  const active = new Set(
    details.patients
      .filter((patient) => patient.status === "ACTIVE")
      .map((patient) => patient.userId),
  );
  return details.members
    .filter(
      (member) =>
        member.roles.includes("Child") && member.status !== "DISABLED" && active.has(member.id),
    )
    .map((member) => member.id);
}

function onOwnRecord(team: ChildCareTeam[], childUserId: number, staffUserId: number): boolean {
  return (
    team
      .find((child) => child.childUserId === childUserId)
      ?.members.some((member) => member.staffUserId === staffUserId && member.onOwnRecord) ?? false
  );
}

/** An add lands once every child it wrote to lists the member; a removal once no record does. */
function applied(pending: PendingChange, team: ChildCareTeam[]): boolean {
  return pending.change === "add"
    ? pending.childUserIds.every((childUserId) =>
        onOwnRecord(team, childUserId, pending.staffUserId),
      )
    : !team.some((child) =>
        child.members.some((member) => member.staffUserId === pending.staffUserId),
      );
}

/**
 * Each child's derived care team. Add and remove are family-wide Healthie writes queued through
 * the outbox, so a changed member reads as pending until a refetched team shows the change.
 */
function CareTeamSection({ details }: { details: FamilyDetails }) {
  const headingId = useId();
  const { showToast } = useToast();
  const [team, setTeam] = useState(details.careTeam);
  const [pending, setPending] = useState<PendingChange[]>([]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<CareTeamMember | null>(null);
  const [changingCoverage, setChangingCoverage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const refreshSeq = useRef(0);

  async function refresh(isCurrent: () => boolean = () => true) {
    const seq = ++refreshSeq.current;
    const result = await familyCareTeam(details.familyId);
    if (seq !== refreshSeq.current || !isCurrent()) {
      return;
    }
    const children = result.ok ? result.value.children : null;
    if (children !== null) {
      setTeam(children);
    }
    const now = Date.now();
    const landed = (change: PendingChange) => children !== null && applied(change, children);
    const stale = pendingRef.current.filter((change) => !landed(change) && expired(change, now));
    setPending((current) => current.filter((change) => !landed(change) && !expired(change, now)));
    if (stale.length > 0) {
      setError(
        `${stale.map((change) => change.name).join(", ")}: still not updated after two minutes. Refresh the page, or ask an admin to check.`,
      );
    }
  }

  usePolling(
    async (isCurrent) => {
      await refresh(isCurrent);
      return true;
    },
    { delayMillis: CARE_TEAM_POLL_MILLIS, active: pending.length > 0 },
  );

  function queued(change: PendingChange, confirmation: string) {
    setPending((now) => [...now.filter((p) => p.staffUserId !== change.staffUserId), change]);
    showToast("success", confirmation);
  }

  async function changeCoverage(assignmentIds: string[], coverage: Coverage) {
    setChangingCoverage(true);
    setError(null);
    for (const assignmentId of assignmentIds) {
      const result = await setCoverage(assignmentId, coverage);
      if (!result.ok) {
        setError(result.error.message);
        break;
      }
    }
    await refresh();
    setChangingCoverage(false);
  }

  const pendingIds = new Set(pending.map((change) => change.staffUserId));
  const membersById = new Map(details.members.map((member) => [member.id, member]));

  return (
    <section
      aria-labelledby={headingId}
      className="enter space-y-3"
      style={{ "--enter-delay": "110ms" } as CSSProperties}
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id={headingId}
          className="text-sm font-medium uppercase tracking-wide text-text-tertiary"
        >
          Care team
        </h2>
        <Button
          variant="secondary"
          size="sm"
          aria-label="Add care team member"
          onClick={() => setAdding(true)}
        >
          <UserPlus size={14} aria-hidden="true" />
          Add
        </Button>
      </div>

      {error !== null && <ErrorMessage message={error} className="mb-0" />}

      <div aria-live="polite">
        {pending.length > 0 && (
          <ul className="space-y-1.5">
            {pending.map((change) => (
              <li
                key={change.staffUserId}
                className="border border-border bg-bg-elevated shadow-card flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm text-text-secondary"
              >
                <Spinner size="xs" />
                <span>
                  {change.change === "add" ? "Adding" : "Removing"} {change.name}
                </span>
                <Badge tone="info">Pending</Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      {team.length === 0 ? (
        <p className="text-sm text-text-tertiary">No one in this family is in care yet.</p>
      ) : (
        <ol className="grid gap-4 lg:grid-cols-2">
          {team.map((child) => (
            <li key={child.childUserId} className="border border-border bg-bg-elevated shadow-card rounded-3xl p-5">
              <h3 className="font-display text-lg leading-tight text-text-primary">
                {childName(child.childUserId, membersById)}
              </h3>
              {child.members.length === 0 ? (
                <p className="mt-2 text-sm text-text-tertiary">No care team yet.</p>
              ) : (
                <ul className="mt-2 divide-y divide-border/60">
                  {child.members.map((member) => (
                    <CareTeamMemberRow
                      key={member.staffUserId}
                      member={member}
                      source={memberSource(member, membersById)}
                      isPending={pendingIds.has(member.staffUserId)}
                      coverageLocked={changingCoverage}
                      onRemove={() => setRemoving(member)}
                      onCoverage={(coverage) =>
                        void changeCoverage(
                          familyRecords(team, member.staffUserId)
                            .filter((record) => record.coverage !== coverage)
                            .map((record) => record.id),
                          coverage,
                        )
                      }
                    />
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}

      <AddCareTeamMemberModal
        isOpen={adding}
        familyId={details.familyId}
        onClose={() => setAdding(false)}
        onQueued={(staffUserId, name) => {
          setAdding(false);
          queued(
            {
              staffUserId,
              name,
              change: "add",
              since: Date.now(),
              childUserIds: activeChildIds(details).filter(
                (childUserId) => !onOwnRecord(team, childUserId, staffUserId),
              ),
            },
            `${name} added. ${MESSAGING_TIMING}`,
          );
        }}
      />

      <RemoveCareTeamMemberModal
        member={removing}
        familyId={details.familyId}
        onClose={() => setRemoving(null)}
        onQueued={(member) => {
          setRemoving(null);
          queued(
            {
              staffUserId: member.staffUserId,
              name: memberName(member),
              change: "remove",
              since: Date.now(),
            },
            `${memberName(member)} removed. The family can no longer message them.`,
          );
        }}
      />
    </section>
  );
}

/** Why a member the child's own record does not list is on the team: a parent's record lists them. */
function memberSource(
  member: CareTeamMember,
  membersById: Map<number, FamilyMemberDetail>,
): string {
  if (member.onOwnRecord) {
    return "";
  }
  return member.viaParentIds
    .map((parentId) => `On ${membersById.get(parentId)?.firstName ?? "a parent"}'s care team`)
    .join(" · ");
}

/** Every record that puts the staff member on one of the family's teams, once each. */
function familyRecords(team: ChildCareTeam[], staffUserId: number): CareTeamAssignment[] {
  const byId = new Map<string, CareTeamAssignment>();
  for (const child of team) {
    for (const member of child.members) {
      if (member.staffUserId === staffUserId) {
        member.assignments.forEach((record) => byId.set(record.id, record));
      }
    }
  }
  return [...byId.values()];
}

function CareTeamMemberRow({
  member,
  source,
  isPending,
  coverageLocked,
  onRemove,
  onCoverage,
}: {
  member: CareTeamMember;
  source: string;
  isPending: boolean;
  coverageLocked: boolean;
  onRemove: () => void;
  onCoverage: (coverage: Coverage) => void;
}) {
  const coverage = member.familyCoverage;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text-primary">
          <Link to={`/accounts/${member.staffUserId}`} className="hover:underline">
            {memberName(member)}
          </Link>
        </p>
        <p className="text-xs text-text-tertiary">
          {[statusLabel(member.kind), source].filter(Boolean).join(" · ")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {member.assignments.some((a) => a.primary) && <Badge tone="info">Primary provider</Badge>}
        {coverage !== null && !isPending ? (
          <ToggleButton
            className="h-9"
            ariaLabel={`${memberName(member)}'s coverage of this family`}
            disabled={coverageLocked}
            options={COVERAGE_OPTIONS}
            value={coverage}
            onChange={(value) => onCoverage(value as Coverage)}
          />
        ) : (
          coverage !== null && <Badge>{coverageLabel(coverage)} coverage</Badge>
        )}
        <GuardedButton
          locked={isPending}
          hint={IN_FLIGHT}
          variant="ghost"
          ariaLabel={`Remove ${memberName(member)}`}
          onClick={onRemove}
        >
          Remove
        </GuardedButton>
      </div>
    </li>
  );
}

function AddCareTeamMemberModal({
  isOpen,
  familyId,
  onClose,
  onQueued,
}: {
  isOpen: boolean;
  familyId: number;
  onClose: () => void;
  onQueued: (staffUserId: number, name: string) => void;
}) {
  const [staffId, setStaffId] = useState("");
  const [coverage, setCoverageChoice] = useState<Coverage>("SUPPORT");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const found = useRef<Record<string, AccountSummary>>({});

  useEffect(() => {
    if (isOpen) {
      setStaffId("");
      setCoverageChoice("SUPPORT");
      setError(null);
    }
  }, [isOpen]);

  // nexus refuses a key it already saw with a different body, so a changed choice is a new add.
  useEffect(() => {
    setIdempotencyKey(newIdempotencyKey());
  }, [isOpen, staffId, coverage]);

  const fetchStaff = useCallback(async (q: string) => {
    const result = await searchAccounts({ q, status: "ACTIVE", scope: "care_team" });
    if (!result.ok) {
      return [];
    }
    return result.value.items.map((account) => {
      found.current[String(account.id)] = account;
      return {
        value: String(account.id),
        label: fullName(account.firstName, account.lastName, `Account ${account.id}`),
        description: account.roles.map(roleLabel).join(" · "),
      };
    });
  }, []);

  const chosen = found.current[staffId] ?? null;
  const coordinator = chosen?.careTeamKind === "CARE_COORDINATOR";
  const name = chosen === null ? "" : fullName(chosen.firstName, chosen.lastName);

  async function add(staff: AccountSummary) {
    setSaving(true);
    setError(null);
    const result = await addToCareTeam(
      familyId,
      staff.id,
      coordinator ? coverage : null,
      idempotencyKey,
    );
    setSaving(false);
    if (result.ok) {
      onQueued(staff.id, name);
    } else {
      setError(refusalMessage(result.error));
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add to care team" size="sm">
      <div className="space-y-4 px-6 py-5">
        <Select
          label="Care-team member"
          value={staffId}
          onChange={setStaffId}
          fetchOptions={fetchStaff}
          searchable
          placeholder="Find a care-team member by name…"
        />
        {coordinator && (
          <CoverageChoice value={coverage} onChange={setCoverageChoice} />
        )}
        <p className="text-sm leading-relaxed text-text-secondary">
          {`${chosen === null ? "They" : name} joins the care team of every child in care in this family. ${MESSAGING_TIMING}`}
        </p>
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving || chosen === null}
            onClick={() => chosen !== null && void add(chosen)}
          >
            <BusyLabel busy={saving} busyText="Adding…">
              Add to care team
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RemoveCareTeamMemberModal({
  member,
  familyId,
  onClose,
  onQueued,
}: {
  member: CareTeamMember | null;
  familyId: number;
  onClose: () => void;
  onQueued: (member: CareTeamMember) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setError(null);
  }, [member]);

  async function remove(target: CareTeamMember) {
    setSaving(true);
    setError(null);
    const result = await removeFromCareTeam(familyId, target.staffUserId);
    setSaving(false);
    if (result.ok) {
      onQueued(target);
    } else {
      setError(refusalMessage(result.error));
    }
  }

  return (
    <Modal isOpen={member !== null} onClose={onClose} title="Remove from care team" size="sm">
      <div className="space-y-4 px-6 py-5">
        <p className="text-sm leading-relaxed text-text-secondary">
          {member === null
            ? ""
            : `${memberName(member)} leaves this family's care team. The family will no longer be able to message them.`}
        </p>
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving}
            onClick={() => member !== null && void remove(member)}
          >
            <BusyLabel busy={saving} busyText="Removing…">
              Remove from care team
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
